// Task 17: the sanctioned backward edge's UI — ⋯ → Send back for review.
// Consumes POST /lifecycle/:type/:id/send-back (Task 12). Reason is required
// by the endpoint (400), so the client enforces it before submitting. On
// success the ['lifecycle', ...] and ['review-queue', ...] caches are
// invalidated and the user lands on Pending Review (/review-queue).
// Worker rejections map to human copy; a pre-redeploy 404 degrades with an
// honest "not deployed yet" message instead of fake success.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Undo2, X } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from './Toast';
import { tr } from '../lib/i18nHelpers';

export type SendBackDocType = 'bank_statement' | 'card_statement' | 'invoice';

interface DocSendBackMenuProps {
  type: SendBackDocType;
  id: string;
  /** Only render the ⋯ trigger when the doc is genuinely send-back-eligible. */
  enabled: boolean;
  /** Extra query keys to invalidate alongside lifecycle + review-queue. */
  extraInvalidate?: unknown[][];
  onSent?: (voidedVouchers: number) => void;
}

/** Human copy for every guarded failure the endpoint can produce. */
export function humanSendBackError(message: string): string {
  if (/unlink the payment/i.test(message))
    return tr(
      "This invoice is already settled — its payment was confirmed. Unlink the payment first, then you can send it back for re-review.",
      '此發票已結算——款項配對已確認。請先解除款項配對，然後才可退回覆核。',
      '此发票已结算——款项配对已确认。请先解除款项配对，然后才可退回复核。');
  if (/reconciled/i.test(message))
    return tr(
      "One of this document's vouchers has already been reconciled with a bank line. Unlink the bank match first, then send it back.",
      '此文件有一筆過賬分錄已完成銀行對賬。請先解除銀行配對，然後退回覆核。',
      '此文件有一笔过账分录已完成银行对账。请先解除银行配对，然后退回复核。');
  // 404 discrimination is case-sensitive and ordered: the published endpoint
  // answers an unknown doc with the JSON body `{"error":"not found"}` (lower
  // case), while a pre-redeploy route-missing 404 leaves only the statusText
  // 'Not Found' (capital F). An /i flag here would make the doc-missing
  // branch unreachable — the wrong honest copy for the wrong failure.
  if (/^not found$/.test(message))
    return tr(
      'Could not find this document — it may have been deleted since this page opened. Refresh and try again.',
      '找不到此文件——可能已在本頁開啟後被刪除。請重新整理後再試。',
      '找不到此文件——可能已在本页开启后被删除。请刷新后再试。');
  if (/^Not Found$/.test(message))
    return tr(
      'The deployed API build does not support send-back yet. Nothing was changed — this will work once the backend is redeployed.',
      '目前部署的 API 版本尚未支援退回功能。未有任何更改——後端重新部署後即可使用。',
      '目前部署的 API 版本尚未支持退回功能。未有任何更改——后端重新部署后即可使用。');
  if (/cannot send back from/i.test(message))
    return tr(
      "This document has moved past the review stage and can no longer be sent back. Open the lifecycle view to see its current state.",
      '此文件已越過覆核階段，無法退回。可開啟生命週期檢視查看現況。',
      '此文件已越过复核阶段，无法退回。可开启生命周期视图查看现况。');
  if (/Invalid lifecycle transition/i.test(message))
    return tr(
      "The document changed while you were working (it is no longer at the stage send-back expects). Refresh the page and check its current state before trying again.",
      '處理期間文件狀態已被更改（不再處於可退回的階段）。請重新整理頁面查看現況後再試。',
      '处理期间文件状态已被更改（不再处于可退回的阶段）。请刷新页面查看现况后再试。');
  return tr(
    'Send-back failed and nothing was changed.',
    '退回失敗，未有任何更改。',
    '退回失败，未有任何更改。');
}

export default function DocSendBackMenu({ type, id, enabled, extraInvalidate, onSent }: DocSendBackMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const menuRef = useRef<HTMLDivElement | null>(null);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Close the ⋯ popover when clicking anywhere else
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const sendBackMut = useMutation({
    mutationFn: (r: string) =>
      api(`/lifecycle/${type}/${id}/send-back`, { method: 'POST', body: { reason: r } }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['lifecycle'] });
      qc.invalidateQueries({ queryKey: ['review-queue'] });
      for (const key of extraInvalidate ?? []) qc.invalidateQueries({ queryKey: key });
      const voided = Number(res?.voided_vouchers ?? 0);
      toast.success(
        voided > 0
          ? tr(`Sent back for review — ${voided} linked voucher${voided === 1 ? '' : 's'} voided.`, `已退回覆核——已作廢 ${voided} 筆連結的過賬分錄。`, `已退回复核——已作废 ${voided} 笔连结的过账分录。`)
          : tr('Sent back for review.', '已退回覆核。', '已退回复核。'),
        6000,
      );
      setMenuOpen(false);
      setDialogOpen(false);
      setReason('');
      onSent?.(voided);
      nav('/review-queue');
    },
    onError: (err: any) => {
      // api.ts throws Error(err.error || 'Request failed') with e.body
      // carrying the parsed response — the body's error field is authoritative.
      toast.error(humanSendBackError(err?.body?.error ?? err?.message ?? ''), 8000);
    },
  });

  if (!enabled) return null;

  return (
    <div className="relative z-30" ref={menuRef}>
      <button
        type="button"
        aria-label={tr('More actions', '更多操作', '更多操作')}
        onClick={() => setMenuOpen(o => !o)}
        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {menuOpen && (
        <div className="absolute right-0 top-9 z-40 bg-card border rounded-lg shadow-lg py-1 min-w-[11rem]">
          <button
            type="button"
            onClick={() => { setMenuOpen(false); setDialogOpen(true); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
          >
            <Undo2 className="h-3.5 w-3.5 text-muted-foreground" />
            {tr('Send back for review', '退回覆核', '退回复核')}
          </button>
        </div>
      )}

      {/* Reason prompt — the endpoint rejects a blank reason with 400, so the
          dialog keeps Send disabled until something is typed. */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogOpen(false)}>
          <div className="bg-card border rounded-xl p-5 w-full max-w-md mx-4 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <span className="text-2xl">↩️</span>
              <h3 className="font-bold">{tr('Send back for review', '退回覆核', '退回复核')}</h3>
              <button onClick={() => setDialogOpen(false)} className="ml-auto p-1 hover:bg-muted rounded text-muted-foreground" aria-label={tr('Close', '關閉', '关闭')}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              {tr(
                "Sending this document back re-opens its review: the journal entries it generated are voided and leave the books, and the document reappears in Pending Review for correction.",
                '退回即重新開啟覆核：由此文件產生的日記帳分錄會作廢並離開帳簿，文件會重新出現在待審核清單供修正。',
                '退回即重新开启复核：由此文件产生的日记账分录会作废并离开账簿，文件会重新出现在待审核清单供修正。'
              )}
            </p>
            <label className="block">
              <span className="text-xs text-muted-foreground">{tr('Reason (required)', '原因（必填）', '原因（必填）')}</span>
              <textarea
                aria-label={tr('Reason', '原因', '原因')}
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                placeholder={tr('e.g. Closing balance does not match the PDF', '例如：期末結餘與 PDF 不符', '例如：期末结余与 PDF 不符')}
                className="mt-1 w-full px-3 py-2 border rounded-md text-sm bg-background resize-none"
              />
            </label>
            {sendBackMut.isError && (
              <p className="text-xs text-red-900 dark:text-red-200">{humanSendBackError((sendBackMut.error as any)?.body?.error ?? (sendBackMut.error as any)?.message ?? '')}</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setDialogOpen(false)} className="px-4 py-2 border rounded-md text-sm hover:bg-muted">
                {tr('Cancel', '取消', '取消')}
              </button>
              <button
                onClick={() => sendBackMut.mutate(reason.trim())}
                disabled={!reason.trim() || sendBackMut.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:opacity-90 disabled:opacity-50"
              >
                {sendBackMut.isPending ? tr('Sending back…', '退回中…', '退回中…') : tr('Send back', '退回', '退回')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
